import type { Address } from "viem"

import {
  FixedStakedObol__factory as FixedStakedObolFactory,
  ObolTestToken__factory as ObolTestTokenFactory,
  RebasingStakedObol__factory as RebasingStakedObolFactory,
} from "@/contracts/bindings"
import {
  getMainnetChainId,
  getSepoliaChainId,
} from "@/web3/helpers/chainReference"
import { ChainId } from "@/web3/constants/chains"

enum Environment {
  Prod = "https://api.tally.xyz",
  Staging = "https://api.staging.tally.xyz",
}

export type LSTToken<Factory = any> = {
  address: string
  symbol: string
  name: string
  decimals: number
  chainId: string
  id: string
  factory: Factory
}

export type OrgToken = LSTToken<typeof ObolTestTokenFactory>

export type FixedLSTToken = LSTToken<typeof FixedStakedObolFactory>

export type RebasingLSTToken = LSTToken<typeof RebasingStakedObolFactory>

type UIConfig = {
  tallyPoints: boolean
  displayDRS: boolean
}
export type LSTData = {
  orgToken: OrgToken
  lstToken: FixedLSTToken
  rebasingLSTToken: RebasingLSTToken
  chainId: string
  uiConfig: UIConfig
  useGetAliasedDelegateeFromRebasingContract: boolean
  supportsPermitAndStake: boolean
  delegatesEligibilityOracleAddress?: Address
  drsDefaultThreshold: number
  allowNonEligibleDelegates: boolean
}

const withDefaults = (data: Partial<LSTData>): LSTData => {
  return {
    uiConfig: {
      tallyPoints: false,
      displayDRS: false,
      ...(data?.uiConfig ?? {}),
    },
    useGetAliasedDelegateeFromRebasingContract: false,
    supportsPermitAndStake: true,
    delegatesEligibilityOracleAddress: undefined,
    drsDefaultThreshold: 0,
    allowNonEligibleDelegates: true,
    ...data,
  } as LSTData
}

// key is the org ID
const LST_CONSTANTS: Record<Environment, Record<string, LSTData>> = {
  [Environment.Staging]: {
    // OBOL LST TEST: https://staging.tally.xyz/gov/obol-test-st-gov
    "2580756208071739096": withDefaults({
      chainId: getSepoliaChainId(),
      orgToken: {
        // https://sepolia.etherscan.io/address/0x830B162043b41908840aC8328Ce867CB6B5C2c74#readContract
        address: "0x830B162043b41908840aC8328Ce867CB6B5C2c74",
        symbol: "OBOLTEST2",
        name: "Obol Staker Test Token 2",
        decimals: 18,
        chainId: getSepoliaChainId(),
        id: `${getSepoliaChainId()}:0x830B162043b41908840aC8328Ce867CB6B5C2c74`,
        factory: ObolTestTokenFactory,
      },
      lstToken: {
        address: "0x2D0020Fc5BAA1c2fe02058c2E1eefA38e0e58519",
        symbol: "stOBOLTEST2",
        name: "Staked Obol Test",
        decimals: 18,
        chainId: getSepoliaChainId(),
        id: `${getSepoliaChainId()}:0x2D0020Fc5BAA1c2fe02058c2E1eefA38e0e58519`,
        factory: FixedStakedObolFactory,
      },
      rebasingLSTToken: {
        address: "0xDfdEB974D0A564d7C25610e568c1D309220236BB",
        symbol: "rstOBOLTEST2",
        name: "Rebasing Staked Obol Test",
        decimals: 18,
        chainId: getSepoliaChainId(),
        id: `${getSepoliaChainId()}:0xDfdEB974D0A564d7C25610e568c1D309220236BB`,
        factory: RebasingStakedObolFactory,
      },
      useGetAliasedDelegateeFromRebasingContract: true,
    }),
    // // Rari Chain Staging rari-staking-test
    "2580795246598162076": withDefaults({
      chainId: ChainId.RariChainMainnet,
      // https://mainnet.explorer.rarichain.org/address/0xBcBA0785b51ad5305E5c19471eeE9e87f6d6D976
      orgToken: {
        // https://mainnet.explorer.rarichain.org/address/0xCf78572A8fE97b2B9a4B9709f6a7D9a863c1b8E0
        address: "0xCf78572A8fE97b2B9a4B9709f6a7D9a863c1b8E0",
        symbol: "RARI",
        name: "RARI",
        decimals: 18,
        chainId: ChainId.RariChainMainnet,
        id: `${ChainId.RariChainMainnet}:0xCf78572A8fE97b2B9a4B9709f6a7D9a863c1b8E0`,
        factory: ObolTestTokenFactory,
      },
      //https://mainnet.explorer.rarichain.org/address/0x6a74F6b4f4687966aA6b95571f97Cc8216970775
      lstToken: {
        address: "0x6a74F6b4f4687966aA6b95571f97Cc8216970775",
        symbol: "stRARI",
        name: "Rari LST",
        decimals: 18,
        chainId: ChainId.RariChainMainnet,
        id: `${ChainId.RariChainMainnet}:0x6a74F6b4f4687966aA6b95571f97Cc8216970775`,
        factory: FixedStakedObolFactory,
      },
      // https://mainnet.explorer.rarichain.org/address/0xf5b1009B9B5e36346f4A6B586FA35d02996703bf
      rebasingLSTToken: {
        address: "0xf5b1009B9B5e36346f4A6B586FA35d02996703bf",
        symbol: "rstRARI",
        name: "Rebasing Rari LST",
        decimals: 18,
        chainId: ChainId.RariChainMainnet,
        id: `${ChainId.RariChainMainnet}:0xf5b1009B9B5e36346f4A6B586FA35d02996703bf`,
        factory: RebasingStakedObolFactory,
      },
      supportsPermitAndStake: false,
      allowNonEligibleDelegates: false,
    }),
  },
  [Environment.Prod]: {
    // OBOL Mainnet https://tally.xyz/gov/obol/stake/liquid
    "2413388957975839812": withDefaults({
      chainId: getMainnetChainId(),
      orgToken: {
        // https://etherscan.io/address/0x0B010000b7624eb9B3DfBC279673C76E9D29D5F7#readContract
        address: "0x0B010000b7624eb9B3DfBC279673C76E9D29D5F7",
        symbol: "OBOL",
        name: "Obol Network",
        decimals: 18,
        chainId: getMainnetChainId(),
        id: `${getMainnetChainId()}:0x0B010000b7624eb9B3DfBC279673C76E9D29D5F7`,
        factory: ObolTestTokenFactory,
      },
      // https://etherscan.io/address/0x6590cBBCCbE6B83eF3774Ef1904D86A7B02c2fCC#readContract
      lstToken: {
        address: "0x6590cBBCCbE6B83eF3774Ef1904D86A7B02c2fCC",
        symbol: "stOBOL",
        name: "Staked Obol",
        decimals: 18,
        chainId: getMainnetChainId(),
        id: `${getMainnetChainId()}:0x6590cBBCCbE6B83eF3774Ef1904D86A7B02c2fCC`,
        factory: FixedStakedObolFactory,
      },
      // https://etherscan.io/address/0x1932e815254c53B3Ecd81CECf252A5AC7f0e8BeA#readContract
      rebasingLSTToken: {
        address: "0x1932e815254c53B3Ecd81CECf252A5AC7f0e8BeA",
        symbol: "rstOBOL",
        name: "Rebasing Staked Obol",
        decimals: 18,
        chainId: getMainnetChainId(),
        id: `${getMainnetChainId()}:0x1932e815254c53B3Ecd81CECf252A5AC7f0e8BeA`,
        factory: RebasingStakedObolFactory,
      },
    }),
    // Rari Chain Mainnet rari-dao
    "2530797033149695623": withDefaults({
      chainId: ChainId.RariChainMainnet,
      orgToken: {
        // https://mainnet.explorer.rarichain.org/address/0xCf78572A8fE97b2B9a4B9709f6a7D9a863c1b8E0
        address: "0xCf78572A8fE97b2B9a4B9709f6a7D9a863c1b8E0",
        symbol: "RARI",
        name: "RARI",
        decimals: 18,
        chainId: ChainId.RariChainMainnet,
        id: `${ChainId.RariChainMainnet}:0xCf78572A8fE97b2B9a4B9709f6a7D9a863c1b8E0`,
        factory: ObolTestTokenFactory,
      },
      //https://mainnet.explorer.rarichain.org/address/0x6a74F6b4f4687966aA6b95571f97Cc8216970775
      lstToken: {
        address: "0x6a74F6b4f4687966aA6b95571f97Cc8216970775",
        symbol: "stRARI",
        name: "Rari LST",
        decimals: 18,
        chainId: ChainId.RariChainMainnet,
        id: `${ChainId.RariChainMainnet}:0x6a74F6b4f4687966aA6b95571f97Cc8216970775`,
        factory: FixedStakedObolFactory,
      },
      // https://mainnet.explorer.rarichain.org/address/0xf5b1009B9B5e36346f4A6B586FA35d02996703bf
      rebasingLSTToken: {
        address: "0xf5b1009B9B5e36346f4A6B586FA35d02996703bf",
        symbol: "rstRARI",
        name: "Rebasing Rari LST",
        decimals: 18,
        chainId: ChainId.RariChainMainnet,
        id: `${ChainId.RariChainMainnet}:0xf5b1009B9B5e36346f4A6B586FA35d02996703bf`,
        factory: RebasingStakedObolFactory,
      },
      supportsPermitAndStake: false,
      delegatesEligibilityOracleAddress:
        "0xAc5315251B91D2Fa4bf61cE479DF792778A7f464",
      drsDefaultThreshold: 15,
      allowNonEligibleDelegates: false,
      uiConfig: {
        displayDRS: true,
      } as UIConfig,
    }),
  },
}

export function hasLSTStakerData(orgId?: string) {
  return Boolean(getNullishLSTStakerConstants(orgId))
}

// LST-constants safe getter without throwing an error
export function getNullishLSTStakerConstants(orgId?: string) {
  if (!orgId) {
    return null
  }

  const env = process.env.NEXT_PUBLIC_TALLY_API_URL as Environment

  return LST_CONSTANTS[env]?.[orgId] ?? null
}

// LST-constants getter | throws an error if no data is found
export function getLSTStakerConstants(orgId?: string) {
  const data = getNullishLSTStakerConstants(orgId)

  if (!data) {
    throw Error(`No LSTStaker config for orgId "${orgId}"`)
  }

  return data
}
